import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SSOService} from '../../../@core/sso.service';
import {JWTService} from '../../../@core/jwt.service';
import {UserAuthService} from '../../../@core/user-auth.service';

@Component({
    selector: 'app-sso-callback',
    template: `
        <div class="sso-callback">
            <div *ngIf="!error" class="loading">
                <h3>Signing you in...</h3>
                <p>Please wait while we complete your sign-in.</p>
            </div>
            <div *ngIf="error" class="error">
                <h3>Sign-in Error</h3>
                <p>{{error}}</p>
                <button (click)="goToLogin()">Back to Login</button>
            </div>
        </div>
    `,
    styles: [`
        .sso-callback {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            text-align: center;
            color: #fff;
        }
        .loading h3, .error h3 {
            margin-bottom: 1em;
        }
        .error {
            background: rgba(220, 53, 69, 0.2);
            padding: 2em;
            border-radius: 8px;
        }
        button {
            margin-top: 1em;
            padding: 0.5em 1.5em;
            background: transparent;
            border: 1px solid #fff;
            color: #fff;
            cursor: pointer;
            border-radius: 4px;
        }
        button:hover {
            background: #fff;
            color: #333;
        }
    `],
})
export class SSOCallbackComponent implements OnInit {
    error: string | null = null;
    private isPopup = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private ssoService: SSOService,
        private jwtService: JWTService,
        private userAuthService: UserAuthService
    ) {}

    ngOnInit(): void {
        this.isPopup = SSOService.isPopupFlow;

        const code = this.route.snapshot.queryParamMap.get('code');
        const state = this.route.snapshot.queryParamMap.get('state');
        const errorParam = this.route.snapshot.queryParamMap.get('error');
        const errorDescription = this.route.snapshot.queryParamMap.get('error_description');

        if (errorParam) {
            this.handleError(errorDescription || errorParam || 'SSO authentication failed');
            return;
        }

        if (!code) {
            this.handleError('No authorization code received');
            return;
        }

        this.jwtService.authToken = null;
        this.jwtService.refreshToken = null;

        this.ssoService.exchangeCode(code, state || '').subscribe({
            next: response => {
                if (this.isPopup) {
                    this.handlePopupSuccess(response.token, response.refresh_token);
                } else {
                    this.jwtService.setToken(response.token, response.refresh_token, false);
                    this.userAuthService.updateUserInfo().then(() => {
                        this.router.navigate(['/'], {replaceUrl: true});
                    }).catch(() => {
                        this.handleError('Failed to load user information');
                    });
                }
            },
            error: err => {
                this.handleError(err.error?.error || 'SSO authentication failed');
            },
        });
    }

    private handlePopupSuccess(token: string, refreshToken: string): void {
        SSOService.notifySuccess(token, refreshToken);
        setTimeout(() => {
            window.close();
            if (!window.closed) {
                this.error = 'Login successful. You can close this window now.';
            }
        }, 100);
    }

    private handleError(errorMessage: string): void {
        this.error = errorMessage;
        SSOService.notifyError(errorMessage);
    }

    goToLogin(): void {
        window.close();
        // If window.close() doesn't work, navigate to login
        this.router.navigate(['/login']);
    }
}
